import * as React from 'react';
import {Box, Divider, Grid, Paper} from '@material-ui/core';
import {Field} from 'formik';
import TextField from '@material-ui/core/TextField';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
} from 'formik-material-ui-lab';
import {Duration} from 'components/Duration';
import moment from 'moment';
import * as yup from 'yup';
import {useTitle} from 'contexts/TitleContext';
import {ToggleButtonField} from 'components/ToggleButtonField';
import {ButtonBar} from 'components/ButtonBar';
import {BaseForm} from 'components/BaseForm';
import {useParams, useHistory} from 'react-router';
import {useEngineContext} from 'contexts/EngineContext';
import {StartEndDateTime} from '@deckmans/web-shared';
import {FullPageError} from 'components/FullPageError';
import {Shiploader} from '@deckmans/domain';
import {useDataContext} from 'providers/DataContextProvider';
import {useHistoryStateDateRange} from 'hooks/useHistoryStateDateRange';
import {useVessel, useVesselActivity} from 'hooks/useVessel';
import {getLastVesselActivityEndDate} from '@deckmans/domain/lib/util/sortVesselActivities';

interface DirectLoadingActivityType {
  id: string;
  startTime: string;
  endTime: string;
  comments: string;
  train: string;
  tippler: string;
  route: string;
  selectedTrain: string;
}

export function DirectLoadingActivity() {
  const history = useHistory();
  useTitle('Direct loading');
  const {
    master: {routes, trains, tipplers},
  } = useDataContext();

  const {engine, eventCreator} = useEngineContext();
  const {hatchId, activityId} = useParams<{
    hatchId: string;
    activityId: string;
  }>();

  const isEditing = React.useMemo(() => {
    return Boolean(activityId);
  }, [activityId]);

  const vessel = useVessel();
  const reclaimStart = vessel.reclaimStart ?? new Date();
  const validationSchema = yup.object().shape({
    startTime: yup
      .date()
      .required('This field is required')
      .min(
        reclaimStart,
        ` Start time cannot be before vessel start time (${reclaimStart.toDateString()} ${reclaimStart.toLocaleTimeString()})`
      ),
    endTime: yup
      .string()
      .required('This field is required')
      .test(
        'is-greater',
        'End time should be greater than start time',
        function (value) {
          const {startTime} = this.parent;
          return moment(value).isAfter(moment(startTime));
        }
      ),
    train: yup.string().required('This field is required'),
    route: yup.string().required('Select a route'),
    tippler: yup.string().required('Select a tippler'),
  });

  const parentActivity = useVesselActivity(hatchId);

  const shiploader = React.useMemo(() => {
    return parentActivity ? parentActivity.shiploader : Shiploader.UNRECOGNIZED;
  }, [parentActivity]);

  const activity = React.useMemo(() => {
    if (activityId) {
      return parentActivity?.vesselHatchActivityGroup?.activities[activityId];
    }
  }, [activityId, parentActivity]);

  const pvEndDate = getLastVesselActivityEndDate(vessel.activities, shiploader);

  const sd = pvEndDate !== '' ? pvEndDate : reclaimStart;

  const {startDate, endDate} = useHistoryStateDateRange(sd);

  if (!vessel) {
    return <FullPageError error={new Error('No Vessel could be found')} />;
  }
  if (isEditing && !activity) {
    return (
      <FullPageError
        error={new Error('No activities could be found on the vessel')}
      />
    );
  }

  const hatchFinished = !!vessel.activities[hatchId].vesselHatchActivityGroup
    ?.completed;
  return (
    <BaseForm<DirectLoadingActivityType>
      isEditing={isEditing}
      initialValues={
        isEditing
          ? {
              id: activity?.id ?? '',
              startTime: moment(activity?.startDate).format('YYYY-MM-DDTHH:mm'),
              endTime: moment(activity?.endDate).format('YYYY-MM-DDTHH:mm'),
              train: activity?.hatchDirectLoadingActivity?.train ?? '',
              comments: '',
              route: activity?.hatchDirectLoadingActivity?.route ?? '',
              tippler: activity?.hatchDirectLoadingActivity?.tippler ?? '',
              selectedTrain: activity?.hatchDirectLoadingActivity?.train ?? '',
            }
          : {
              id: '',
              startTime: moment(startDate).format('YYYY-MM-DDTHH:mm'),
              endTime: moment(endDate).format('YYYY-MM-DDTHH:mm'),
              train: '',
              comments: '',
              route: '',
              tippler: '',
              selectedTrain: '',
            }
      }
      validationSchema={validationSchema}
      onCreateSubmit={async ({endTime, startTime, train, route, tippler}) => {
        const event = eventCreator.hatchDirectLoadingActivityCreated({
          endDate: moment(endTime).toDate(),
          startDate: moment(startTime).toDate(),
          groupActivityId: hatchId,
          route,
          tippler: tippler,
          train,
          vesselId: vessel.id,
        });
        await engine.process(event);
        // history.replace(`/hatch/view/${vesselId}/${hatchId}`);
        history.goBack();
      }}
      onEditSubmit={async ({endTime, startTime, train, tippler, route}) => {
        const event = eventCreator.hatchDirectLoadingActivityEdited({
          id: activityId,
          endDate: moment(endTime).toDate(),
          startDate: moment(startTime).toDate(),
          groupActivityId: hatchId,
          route,
          tippler: tippler,
          train,
          vesselId: vessel.id,
        });
        await engine.process(event);
        // history.replace(`/hatch/view/${vesselId}/${hatchId}`);
        history.goBack();
      }}
    >
      {({values, submitForm, errors, touched}) => {
        return (
          <Box>
            <Paper elevation={8}>
              <Box py={4} px={2} mt={3}>
                <Grid container spacing={3}>
                  <StartEndDateTime />

                  <Grid item xs={6}>
                    <Box px={1}>
                      <ToggleButtonField
                        label="Tippler"
                        field="tippler"
                        data={tipplers.map((tippler) => ({
                          id: tippler,
                          description: tippler,
                        }))}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    {values.startTime && values.endTime && (
                      <Box px={1}>
                        <Duration
                          startTime={values.startTime}
                          endTime={values.endTime}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Box px={1}>
                      <ToggleButtonField
                        label="Route"
                        field="route"
                        data={routes.map((route) => ({
                          id: route,
                          description: route,
                        }))}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box px={1}>
                      <Field
                        component={Autocomplete}
                        options={trains}
                        defaultValue={
                          isEditing ? values.selectedTrain : undefined
                        }
                        renderOption={(option: string) => option}
                        name="train"
                        getOptionLabel={(option: string) => option}
                        fullWidth
                        renderInput={(
                          params: AutocompleteRenderInputParams
                        ) => {
                          return (
                            <TextField
                              placeholder="Train"
                              label="Train"
                              {...params}
                              error={touched['train'] && !!errors['train']}
                              helperText={
                                touched['train'] && !!errors['train']
                                  ? errors['train']
                                  : ''
                              }
                            />
                          );
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonBar
                      createText="save"
                      saveText="update"
                      isEditing={isEditing}
                      handleSave={submitForm}
                      vessel={vessel}
                      activityId={activityId}
                      title="Delete Direct Loading Activity"
                      subtitle="Are you sure you want to delete this direct loading activity?"
                      hatchFinished={hatchFinished}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        );
      }}
    </BaseForm>
  );
}
